import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  TextField,
  Box,
  Grid,
  Paper,
  Divider,
  IconButton,
  Badge,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Rating,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Star,
  LocationOn,
  Email,
  Phone,
  Close,
  Send,
  CheckCircle,
  Search,
  FilterList,
  Business,
  AttachMoney,
  Person,
  Work,
  Schedule,
  VideoCall,
  ArrowBack,
  Notifications
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const TalentMarketplace = ({collapsed}) => {
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewRequest, setInterviewRequest] = useState({
    preferredDates: [],
    timeSlots: [],
    message: ''
  });
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [rateFilter, setRateFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Styled components
  const StyledCard = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    transition: theme.transitions.create(['box-shadow', 'transform']),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-4px)',
    },
  }));

  const TalentAvatar = styled(Avatar)(({ theme }) => ({
    width: 80,
    height: 80,
    border: `3px solid ${theme.palette.primary.light}`,
  }));

  // Mock talent data
  const talents = [
    {
      id: 1,
      name: "Sarah Johnson",
      title: "Senior Frontend Developer",
      rating: 4.9,
      reviewCount: 127,
      location: "San Francisco, CA",
      hourlyRate: "$85/hr",
      skills: ["React", "TypeScript", "Next.js", "Node.js"],
      experience: "5+ years",
      avatar: "/api/placeholder/150/150",
      description: "Experienced frontend developer with expertise in modern React applications and scalable architectures.",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      availability: "Available",
      projects: 45,
      successRate: 98,
      responseTime: "< 2 hours"
    },
    {
      id: 2,
      name: "Michael Chen",
      title: "Full Stack Engineer",
      rating: 4.8,
      reviewCount: 89,
      location: "Austin, TX",
      hourlyRate: "$90/hr",
      skills: ["Python", "Django", "React", "PostgreSQL"],
      experience: "7+ years",
      avatar: "/api/placeholder/150/150",
      description: "Full stack engineer specializing in Python backends and React frontends with strong database design skills.",
      email: "michael.chen@email.com",
      phone: "+1 (555) 987-6543",
      availability: "Available",
      projects: 62,
      successRate: 96,
      responseTime: "< 1 hour"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      title: "UX/UI Designer",
      rating: 4.9,
      reviewCount: 156,
      location: "New York, NY",
      hourlyRate: "$75/hr",
      skills: ["Figma", "Adobe Creative Suite", "Prototyping", "User Research"],
      experience: "4+ years",
      avatar: "/api/placeholder/150/150",
      description: "Creative UX/UI designer with a passion for user-centered design and modern interfaces.",
      email: "emily.rodriguez@email.com",
      phone: "+1 (555) 456-7890",
      availability: "Busy",
      projects: 73,
      successRate: 99,
      responseTime: "< 4 hours"
    }
  ];

  const timeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM"
  ];

  const allSkills = [...new Set(talents.flatMap(t => t.skills))];
  const allLocations = [...new Set(talents.map(t => t.location))];

  // Filter talents
  const filteredTalents = talents.filter(talent => {
    const matchesSearch = searchTerm === '' || 
      talent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      talent.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSkill = skillFilter === '' || talent.skills.includes(skillFilter);
    const matchesLocation = locationFilter === '' || talent.location === locationFilter;
    
    return matchesSearch && matchesSkill && matchesLocation;
  });

  const handleTalentClick = (talent) => {
    setSelectedTalent(talent);
  };

  const handleInterviewRequest = () => {
    setShowInterviewModal(true);
    setInterviewRequest({
      preferredDates: [],
      timeSlots: [],
      message: ''
    });
  };

  const handleDateChange = (date) => {
    setInterviewRequest(prev => ({
      ...prev,
      preferredDates: prev.preferredDates.includes(date)
        ? prev.preferredDates.filter(d => d !== date)
        : [...prev.preferredDates, date]
    }));
  };

  const handleTimeSlotChange = (slot) => {
    setInterviewRequest(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.includes(slot)
        ? prev.timeSlots.filter(s => s !== slot)
        : [...prev.timeSlots, slot]
    }));
  };

  const submitInterviewRequest = () => {
    if (interviewRequest.preferredDates.length === 0 || interviewRequest.timeSlots.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one date and time slot',
        severity: 'error'
      });
      return;
    }

    const notification = {
      id: Date.now(),
      candidateId: selectedTalent.id,
      candidateName: selectedTalent.name,
      managerName: "John Manager",
      preferredDates: interviewRequest.preferredDates,
      timeSlots: interviewRequest.timeSlots,
      message: interviewRequest.message,
      status: 'pending',
      createdAt: new Date().toLocaleDateString()
    };

    setNotifications(prev => [...prev, notification]);
    setShowInterviewModal(false);
    setSelectedTalent(null);
    
    setSnackbar({
      open: true,
      message: `Interview request sent to ${selectedTalent.name}!`,
      severity: 'success'
    });
  };

  const getNextSevenDays = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh',  maxWidth: '100%',
  marginLeft: collapsed ? "60px" : "240px", }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Talent Marketplace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Find and connect with top-tier talent
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={notifications.length} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Notifications Panel */}
        {notifications.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Interview Requests Sent ({notifications.length})
            </Typography>
            <Stack spacing={1}>
              {notifications.slice(-3).map(notification => (
                <Paper key={notification.id} elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircle color="success" />
                      <Typography variant="body2">
                        Request sent to {notification.candidateName}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {notification.createdAt}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Alert>
        )}

        {selectedTalent ? (
          /* Talent Profile View */
          <Card elevation={2}>
            <CardContent sx={{ p: 4 }}>
              {/* Back Button */}
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setSelectedTalent(null)}
                sx={{ mb: 3 }}
              >
                Back to marketplace
              </Button>

              {/* Request Interview Button - Top */}
              {/* <Box mb={3}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleInterviewRequest}
                  startIcon={<CalendarToday />}
                >
                  Request Interview
                </Button>
              </Box> */}

              {/* Profile Header */}
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4} display="flex" justifyContent="center">
                  <TalentAvatar
                    src={selectedTalent.avatar}
                    alt={selectedTalent.name}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h3" gutterBottom fontWeight={600}>
                    {selectedTalent.name}
                  </Typography>
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    {selectedTalent.title}
                  </Typography>
                  
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Rating value={selectedTalent.rating} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" fontWeight={600}>
                        {selectedTalent.rating}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({selectedTalent.reviewCount} reviews)
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{selectedTalent.location}</Typography>
                    </Box>
                    <Typography variant="h6" color="success.main" fontWeight={600}>
                      {selectedTalent.hourlyRate}
                    </Typography>
                    <Chip 
                      label={selectedTalent.availability}
                      color={selectedTalent.availability === 'Available' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Stack>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedTalent.projects}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Projects
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedTalent.successRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Success Rate
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedTalent.experience}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Experience
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedTalent.responseTime}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Response Time
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>Contact Information</Typography>
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Email fontSize="small" color="action" />
                        <Typography>{selectedTalent.email}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Phone fontSize="small" color="action" />
                        <Typography>{selectedTalent.phone}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Skills */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>Skills & Expertise</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedTalent.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>

              {/* Description */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>About</Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {selectedTalent.description}
                </Typography>
              </Box>

              {/* Request Interview Button - Bottom */}
              <Divider sx={{ my: 3 }} />
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleInterviewRequest}
                  startIcon={<CalendarToday />}
                >
                  Request Interview
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          /* Talent Grid View */
          <Box>
            {/* Filters */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    placeholder="Search talents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Skill</InputLabel>
                    <Select
                      value={skillFilter}
                      label="Skill"
                      onChange={(e) => setSkillFilter(e.target.value)}
                    >
                      <MenuItem value="">All Skills</MenuItem>
                      {allSkills.map(skill => (
                        <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={locationFilter}
                      label="Location"
                      onChange={(e) => setLocationFilter(e.target.value)}
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {allLocations.map(location => (
                        <MenuItem key={location} value={location}>{location}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Hourly Rate</InputLabel>
                    <Select
                      value={rateFilter}
                      label="Hourly Rate"
                      onChange={(e) => setRateFilter(e.target.value)}
                    >
                      <MenuItem value="">All Rates</MenuItem>
                      <MenuItem value="under50">Under $50/hr</MenuItem>
                      <MenuItem value="50to75">$50-75/hr</MenuItem>
                      <MenuItem value="75to100">$75-100/hr</MenuItem>
                      <MenuItem value="over100">Over $100/hr</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Talent Grid */}
            <Grid container spacing={3}>
              {filteredTalents.map((talent) => (
                <Grid item xs={12} sm={6} lg={4} key={talent.id}>
                  <StyledCard onClick={() => handleTalentClick(talent)} elevation={1}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          src={talent.avatar}
                          alt={talent.name}
                          sx={{ width: 56, height: 56, mr: 2 }}
                        />
                        <Box flexGrow={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {talent.name}
                          </Typography>
                          <Typography color="text.secondary" gutterBottom>
                            {talent.title}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Rating value={talent.rating} precision={0.1} readOnly size="small" />
                            <Typography variant="body2">
                              {talent.rating}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Stack spacing={1} mb={2}>
                        <Box display="flex" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2">{talent.location}</Typography>
                          </Box>
                          <Typography variant="h6" color="success.main" fontWeight={600}>
                            {talent.hourlyRate}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {talent.experience} experience
                          </Typography>
                          <Chip 
                            label={talent.availability}
                            color={talent.availability === 'Available' ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {talent.description.substring(0, 120)}...
                      </Typography>

                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {talent.skills.slice(0, 3).map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {talent.skills.length > 3 && (
                          <Chip
                            label={`+${talent.skills.length - 3} more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Interview Request Modal */}
        <Dialog
          open={showInterviewModal}
          onClose={() => setShowInterviewModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                Request Interview with {selectedTalent?.name}
              </Typography>
              <IconButton onClick={() => setShowInterviewModal(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {/* Preferred Dates */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                <CalendarToday sx={{ mr: 1 }} />
                Preferred Interview Dates
              </Typography>
              <Grid container spacing={1}>
                {getNextSevenDays().map((date) => (
                  <Grid item xs={6} sm={4} key={date}>
                    <ToggleButton
                      fullWidth
                      value={date}
                      selected={interviewRequest.preferredDates.includes(date)}
                      onChange={() => handleDateChange(date)}
                      sx={{ p: 2, flexDirection: 'column' }}
                    >
                      <Typography variant="body2">
                        {formatDate(date)}
                      </Typography>
                    </ToggleButton>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Time Slots */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                <AccessTime sx={{ mr: 1 }} />
                Preferred Time Slots
              </Typography>
              <Grid container spacing={1}>
                {timeSlots.map((slot) => (
                  <Grid item xs={6} sm={4} key={slot}>
                    <ToggleButton
                      fullWidth
                      value={slot}
                      selected={interviewRequest.timeSlots.includes(slot)}
                      onChange={() => handleTimeSlotChange(slot)}
                      sx={{ p: 2 }}
                    >
                      <Typography variant="body2">{slot}</Typography>
                    </ToggleButton>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Message */}
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Message (Optional)"
                placeholder="Add any additional details about the interview or project..."
                value={interviewRequest.message}
                onChange={(e) => setInterviewRequest(prev => ({ ...prev, message: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowInterviewModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitInterviewRequest}
              disabled={interviewRequest.preferredDates.length === 0 || interviewRequest.timeSlots.length === 0}
              startIcon={<Send />}
            >
              Send Interview Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TalentMarketplace;